import React, { useEffect, useRef, useState } from "react";
import axios from 'src/utils/axios';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    TextField,
    Avatar,
    Paper,
    Stack,
    Tooltip,
    CircularProgress,
    Snackbar,
    Alert,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { keyframes } from "@mui/system";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`;


function TypingIndicator() {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                px: 1.5,
                py: 1,
            }}
        >
            {[0, 0.2, 0.4].map((delay, i) => (
                <Box
                    key={i}
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "100%",
                        bgcolor: "#888383de",
                        animation: `${bounce} 1.4s infinite ease-in-out`,
                        animationDelay: `${delay}s`
                    }}
                />
            ))}
        </Box>
    );
}

function TeknisyenGPTDialog({ open, onClose, bankaNo }) {
    const endRef = useRef(null);
    const theme = useTheme();

    const [documentLoading, setDocumentLoading] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [convId, setConvId] = useState(null);
    const [feedbackStates, setFeedbackStates] = useState({});
    const [isTokenLoading, setIsTokenLoading] = useState(false);

    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [snackSeverity, setSnackSeverity] = useState("success");

    const nowHHmm = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (!open) return;
        const tokenRaw = localStorage.getItem("tk_token_info");
        if (!tokenRaw || isTokenAboutToExpire()) {
            getNewToken();
        }
    }, [open]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping, open, feedbackStates]);

    useEffect(() => {
        if (open && bankaNo) {
            const conv = getConversationByBankaNo(bankaNo);
            if (conv) {
                setConvId(conv.conv_id);
                const mapped = conv.KonusmaGecmisi.flatMap((msg, i) => ([
                    {
                        id: i + "_c",
                        sender: "customer",
                        text: msg.soru,
                        time: formatTime(msg.tarih)
                    },
                    {
                        id: i + "_a",
                        sender: "agent",
                        text: msg.cevap,
                        time: formatTime(msg.tarih),
                        documents: msg.documents || []
                    }
                ]));
                setMessages(mapped);
            } else {
                setMessages([]);
                setConvId(null);
            }
        }
    }, [open, bankaNo]);

    const formatTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    const getAllConversations = () => {
        const data = localStorage.getItem("conversations");
        return data ? JSON.parse(data) : [];
    };

    const getConversationByBankaNo = (no) => {
        const data = getAllConversations();
        return data.find(c => String(c.BankaNo) === String(no)) || null;
    };

    const saveConversation = (no, conv_id, soru, cevap, documents = []) => {
        const allConvs = getAllConversations();
        let existingConversation = allConvs.find(c => String(c.BankaNo) === String(no));

        if (!existingConversation) {
            existingConversation = {
                BankaNo: no,
                conv_id: conv_id || null,
                KonusmaGecmisi: []
            };
            allConvs.push(existingConversation);
        }

        if (conv_id && !existingConversation.conv_id) {
            existingConversation.conv_id = conv_id;
        }

        if (soru || cevap) {
            existingConversation.KonusmaGecmisi.push({
                tarih: new Date().toISOString(),
                soru,
                cevap,
                documents
            });
        }
        localStorage.setItem("conversations", JSON.stringify(allConvs));
    };

    const clearConversation = (no) => {
        const allConvs = getAllConversations();
        const updated = allConvs.filter(c => String(c.BankaNo) !== String(no));
        localStorage.setItem("conversations", JSON.stringify(updated));
        setMessages([]);
        setConvId(null);
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text) return;

        const cMsg = {
            id: Date.now() + "_c",
            sender: "customer",
            text,
            time: nowHHmm()
        };
        setMessages((prev) => [...prev, cMsg]);
        setInput("");
        setIsTyping(true);

        refreshTokenIfNeeded();
        const tokenRaw = localStorage.getItem("tk_token_info");
        const tokenInfo = tokenRaw ? JSON.parse(tokenRaw) : {};

        const data = {
            input: text,
            conv_id: convId,
            bankNo: bankaNo,
            accessToken: tokenInfo.accessToken
        }

        axios.post("/ChatWithProduct", data)
            .then((res) => {
                const data = res?.data ?? {};
                const reply = data.Response;
                const newConvId = data.ConvId ?? null;
                const documents = data.Documents ?? [];

                if (newConvId && !convId) {
                    setConvId(newConvId);
                }

                const aMsg = {
                    id: Date.now() + "_a",
                    sender: "agent",
                    text: reply,
                    time: nowHHmm(),
                    documents: documents
                };
                setMessages((prev) => [...prev, aMsg]);
                saveConversation(bankaNo, newConvId, text, reply, documents);
            })
            .catch((ex) => {
                console.error("Hata:", ex);
                const errMsg = {
                    id: Date.now() + "_err",
                    sender: "agent",
                    text: "Şu anda isteğinizi işlerken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.",
                    time: nowHHmm()
                };
                setMessages((prev) => [...prev, errMsg]);
            })
            .finally(() => {
                setIsTyping(false);
            });
    };

    const openDocument = (bankNo, pageNo) => {
        const docKey = `${bankNo}-${pageNo}`;
        setDocumentLoading(docKey);
        axios.get(`/GetTeknisyenKocuRelatedDocument/${bankNo}`)
            .then((response) => {
                const base64Data = response.data[0].DocumentData;
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
            })
            .catch((error) => {
                console.error("Döküman açılırken hata oluştu:", error);
                setSnackSeverity("error");
                setSnackMessage("Döküman açılırken hata oluştu.");
                setSnackOpen(true);
            })
            .finally(() => {
                setDocumentLoading(null);
            });
    };

    function convertMarkdown(md) {
        if (!md) return "";
        md = md
            .replace(/\n{2,}/g, "\n")      // fazla boş satır → tek satır
            .replace(/^\s+|\s+$/g, "");    // baştaki ve sondaki boşlukları temizle

        let html = md;

        html = html.replace(/^\s*#### (.*)$/gm, "<h4>$1</h4>");
        html = html.replace(/^\s*### (.*)$/gm, "<br/><h3>$1</h3>");
        html = html.replace(/^\s*## (.*)$/gm, "<h2>$1</h2>");
        html = html.replace(/^\s*# (.*)$/gm, "<h1>$1</h1>");

        html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
        return html;
    }

    const handleFeedback = (msgId, type) => {
        const isLiked = type === "like" ? 1 : 0;
        setFeedbackStates(prev => ({
            ...prev,
            [msgId]: { ...(prev[msgId] || {}), isLiked: isLiked }
        }));
    };

    const handleReasonChange = (msgId, value) => {
        setFeedbackStates(prev => ({
            ...prev,
            [msgId]: { ...(prev[msgId] || {}), comment: value }
        }));
    };

    const sendFeedback = (msgId) => {
        const feedbackData = feedbackStates[msgId];
        if (!feedbackData || !feedbackData.comment || feedbackData.comment.length <= 0) {
            setSnackSeverity("error");
            setSnackMessage("Lütfen bir yorum giriniz.");
            setSnackOpen(true);
            return;
        }

        const answerMsg = messages.find(x => x.id === msgId);
        const questionMsg = [...messages]
            .slice(0, messages.indexOf(answerMsg))
            .reverse()
            .find(x => x.sender === "customer");

        const data = {
            BankNumbers: bankaNo,
            ConvId: convId,
            Question: questionMsg?.text || "",
            Answer: answerMsg?.text || "",
            IsLiked: feedbackData.isLiked,
            FeedbackComment: feedbackData.comment || ""
        };

        axios.post("/SetTeknisyenKocuFeedbackSend", data)
            .then((response) => {
                const resData = response.data;
                if (resData.ErrorCode === "0") {
                    setSnackSeverity("success");
                    setSnackMessage(resData.ErrorDescription || "Geri bildirim gönderildi.");
                } else {
                    setSnackSeverity("error");
                    setSnackMessage(resData.ErrorDescription || "Bir hata oluştu");
                }
                setSnackOpen(true);
                setFeedbackStates(prev => ({
                    ...prev,
                    [msgId]: { isLiked: null, comment: "" }
                }));
            }).catch((ex) => {
                setSnackSeverity("error");
                setSnackMessage(ex.message);
                setSnackOpen(true);
            });
    };

    function isTokenAboutToExpire() {
        const jsonData = localStorage.getItem("tk_token_info");
        if (!jsonData) return true;

        let objData;
        try {
            objData = JSON.parse(jsonData);
        } catch {
            return true;
        }

        if (!objData.expiresAt) return true;

        const exp = Number(objData.expiresAt);
        if (Number.isNaN(exp)) return true;

        return Date.now() > exp - 60000;
    }

    const refreshTokenIfNeeded = () => {
        if (isTokenAboutToExpire()) {
            getNewToken();
        }
    }

    const getNewToken = () => {
        setIsTokenLoading(true);
        axios.get('/TeknisyenKocuLogin')
            .then((response) => {
                const data = response.data;
                const tokenInfo = {
                    accessToken: data.access_token,
                    tokenType: data.token_type,
                    expiresAt: Date.now() + data.expires_in * 1000
                };
                localStorage.setItem("tk_token_info", JSON.stringify(tokenInfo));
            })
            .catch((err) => console.error(err))
            .finally(() => setIsTokenLoading(false));
    }

    return (
        <>
            <Snackbar
                open={snackOpen}
                autoHideDuration={3000}
                onClose={() => setSnackOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)} variant="filled" sx={{ width: "100%", borderRadius: 2 }}>
                    {snackMessage}
                </Alert>
            </Snackbar>
            <Dialog
                open={open}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        overflow: 'hidden',
                        height: isMobile ? '100%' : '80vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        p: 2,
                        bgcolor: '#ffffff',
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', color: '#FF9800', width: 40, height: 40 }}>
                            <SmartToyIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                                Teknisyen Koçu
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50' }} />
                                Çevrimiçi
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        flex: 1,
                        bgcolor: '#f4f6f8',
                        p: 2,
                        pt: 4,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <Box sx={{ width: '100%', mt: 1 }} />
                    {messages.map((m) => {
                        const isAgent = m.sender === 'agent';
                        const isCustomer = m.sender === 'customer';

                        return (
                            <Box
                                key={m.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: isCustomer ? 'flex-end' : 'flex-start',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    pl: isCustomer ? 2 : 0,
                                    pr: isAgent ? 2 : 0,
                                }}
                            >
                                {isAgent && (
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#009688', fontSize: '1rem', mt: 0 }}>
                                        <SmartToyIcon fontSize="inherit" />
                                    </Avatar>
                                )}
                                <Box sx={{ maxWidth: { xs: '100%', md: '75%' } }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: isCustomer ? '12px 0px 12px 12px' : '0px 12px 12px 12px',
                                            bgcolor: isCustomer ? '#FF9800' : '#ffffff',
                                            color: isCustomer ? '#fff' : 'text.primary',
                                            position: 'relative',
                                            overflow: 'visible',
                                            filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.1))',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                right: isCustomer ? '-9px' : 'auto',
                                                left: isAgent ? '-9px' : 'auto',
                                                width: 0,
                                                height: 0,
                                                borderTop: `10px solid #ffffff`,
                                                borderRight: isCustomer ? '10px solid transparent' : 'none',
                                                borderLeft: isAgent ? '10px solid transparent' : 'none',
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            component="div"
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                fontSize: '0.90rem',
                                                lineHeight: 1.5,
                                                '& a': { color: isCustomer ? '#fff' : 'primary.main', textDecoration: 'underline' }
                                            }}
                                        >
                                            <span dangerouslySetInnerHTML={{ __html: convertMarkdown(m.text) }} />
                                        </Typography>
                                        {isAgent && m.documents && m.documents.length > 0 && (
                                            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                                <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ opacity: 0.8 }}>
                                                    Bu bilgi için aşağıdaki dökümanlardan yararlanılmıştır:
                                                </Typography>
                                                {m.documents.map((doc, idx) => {
                                                    const isLoading = documentLoading === `${doc.BankNo}-${doc.PageNumber}`;
                                                    return (
                                                        <Paper
                                                            key={idx}
                                                            variant="outlined"
                                                            onClick={() => openDocument(doc.BankNo, doc.PageNumber)}
                                                            sx={{
                                                                p: 0.8,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                cursor: isLoading ? 'wait' : 'pointer',
                                                                bgcolor: '#f9fafb',
                                                                border: '1px solid #eee',
                                                                transition: '0.2s',
                                                                '&:hover': { bgcolor: '#f0f0f0', borderColor: '#ccc' }
                                                            }}
                                                        >
                                                            {isLoading ? <CircularProgress size={14} /> : <DescriptionIcon color="error" fontSize="small" />}
                                                            <Typography variant="caption" noWrap sx={{ maxWidth: 180, color: 'text.primary', fontWeight: 500 }}>
                                                                {doc.DocumentDescription}
                                                            </Typography>
                                                        </Paper>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                        <Box sx={{ height: 5 }} />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                textAlign: 'right',
                                                fontSize: '0.7rem',
                                                opacity: 0.8,
                                                color: 'rgba(255,255,255,0.9)'
                                            }}
                                        >
                                            {m.time}
                                        </Typography>
                                    </Paper>

                                    {/* Agent Feedback */}
                                    {/* {isAgent && (
                                        <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Stack direction="row" spacing={0.5}>
                                                <Tooltip title="Faydalı">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleFeedback(m.id, 'like')}
                                                        color={feedbackStates[m.id]?.isLiked === 1 ? "success" : "default"}
                                                        sx={{ padding: 0.5 }}
                                                    >
                                                        {feedbackStates[m.id]?.isLiked === 1 ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Faydalı Değil">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleFeedback(m.id, 'dislike')}
                                                        color={feedbackStates[m.id]?.isLiked === 0 ? "error" : "default"}
                                                        sx={{ padding: 0.5 }}
                                                    >
                                                        {feedbackStates[m.id]?.isLiked === 0 ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>

                                            <Collapse in={feedbackStates[m.id]?.isLiked != null} sx={{ width: '100%' }}>
                                                <Paper sx={{ p: 1.5, mt: 1, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0' }} elevation={0}>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        minRows={2}
                                                        variant="outlined"
                                                        placeholder="Yorumunuz..."
                                                        size="small"
                                                        value={feedbackStates[m.id]?.comment || ''}
                                                        onChange={(e) => handleReasonChange(m.id, e.target.value)}
                                                        sx={{
                                                            bgcolor: '#fafafa',
                                                            '& .MuiOutlinedInput-root': { fontSize: '0.875rem' }
                                                        }}
                                                    />
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={() => sendFeedback(m.id)}
                                                            endIcon={<SendIcon sx={{ fontSize: 16 }} />}
                                                            sx={{ textTransform: 'none', borderRadius: 4 }}
                                                        >
                                                            Gönder
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            </Collapse>
                                        </Box>
                                    )} */}
                                </Box>

                                {/* --- CUSTOMER AVATAR --- */}
                                {isCustomer && (
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#FF9800', fontSize: '1rem', mt: 0 }}>
                                        <PersonIcon fontSize="inherit" />
                                    </Avatar>
                                )}
                            </Box>
                        );
                    })}

                    {/* Yazıyor Animasyonu */}
                    {isTyping && (
                        <Box display="flex" alignItems="flex-start" gap={1}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#009688', mt: 0 }}>
                                <SmartToyIcon fontSize="small" />
                            </Avatar>
                            <Paper
                                sx={{
                                    p: 1.5,
                                    borderRadius: '0px 12px 12px 12px',
                                    bgcolor: '#fff',
                                    filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.1))',
                                    position: 'relative',
                                    overflow: 'visible',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: '-10px',
                                        width: 0,
                                        height: 0,
                                        borderTop: '10px solid #ffffff',
                                        borderLeft: '10px solid transparent',
                                    }
                                }}
                            >
                                <TypingIndicator />
                            </Paper>
                        </Box>
                    )}
                    <div ref={endRef} />
                </DialogContent>
                <DialogActions sx={{ p: 1, bgcolor: '#fff', borderTop: '1px solid #f0f0f0' }}>
                    <Stack direction="row" spacing={1} width="100%" alignItems="center">
                        <Tooltip title="Yeni Sohbet Başlat">
                            <IconButton
                                onClick={() => clearConversation(bankaNo)}
                                size="medium"
                                sx={{
                                    bgcolor: '#f5f5f5',
                                    '&:hover': { bgcolor: '#ffe0b2', color: '#ef6c00' }
                                }}
                            >
                                <DeleteSweepIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                        <Paper
                            component="form"
                            elevation={0}
                            sx={{
                                p: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                                borderRadius: 5,
                                bgcolor: '#f4f6f8',
                                border: '1px solid transparent',
                                '&:focus-within': {
                                    bgcolor: '#fff',
                                    border: '1px solid #FF9800',
                                    boxShadow: '0 0 0 2px rgba(255, 152, 0, 0.1)'
                                },
                                transition: 'all 0.2s'
                            }}
                        >
                            <TextField
                                variant="standard"
                                fullWidth
                                multiline
                                maxRows={3}
                                placeholder="Mesajınızı yazın..."
                                InputProps={{ disableUnderline: true, style: { fontSize: '0.9rem' } }}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                sx={{ ml: 0.5, flex: 1 }}
                            />
                            <IconButton
                                color="primary"
                                size="medium"
                                sx={{ p: '6px', color: input.trim() ? '#FF9800' : '#ccc' }}
                                onClick={handleSend}
                                disabled={isTokenLoading || !input.trim()}
                            >
                                {isTokenLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
                            </IconButton>
                        </Paper>
                    </Stack>
                </DialogActions>
            </Dialog >
        </>
    );
}

export default TeknisyenGPTDialog;